import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/Shared";
import { CheckCircle2, RefreshCw, XCircle, CircleDashed, Database } from "lucide-react";

const STATUS = {
  ok: { label: "Data available", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400", Icon: CheckCircle2, iconCls: "text-green-600" },
  syncing: { label: "Syncing…", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400", Icon: RefreshCw, iconCls: "text-blue-600 animate-spin" },
  empty: { label: "No data yet", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", Icon: CircleDashed, iconCls: "text-amber-600" },
  error: { label: "Error", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400", Icon: XCircle, iconCls: "text-red-600" },
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? v.slice(0, 10) : d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const SyncMonitor = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let timer;
    const load = async () => {
      const { data: d } = await api.get("/tools/sync-monitor");
      setData(d);
      const anySyncing = d.amazon_running || d.datasets.some((x) => x.status === "syncing");
      timer = setTimeout(load, anySyncing ? 8000 : 60000);
    };
    load();
    return () => clearTimeout(timer);
  }, []);

  if (!data) return <Skeleton className="h-48" />;

  return (
    <section className="space-y-3" data-testid="sync-monitor">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-primary" aria-hidden="true" />
        <h3 className="font-heading text-base font-semibold tracking-tight">Amazon Sync Monitor</h3>
        {data.amazon_running && (
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-0 font-semibold" data-testid="sync-monitor-phase">
            {data.phase || "syncing"}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.datasets.map((d) => {
          const s = STATUS[d.status] || STATUS.empty;
          return (
            <div key={d.key} className="bg-card border border-border rounded-lg p-4 shadow-sm" data-testid={`sync-dataset-${d.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <s.Icon className={`h-4 w-4 shrink-0 ${s.iconCls}`} aria-hidden="true" />
                  <p className="font-semibold text-sm truncate">{d.name}</p>
                </div>
                <Badge className={`${s.cls} border-0 font-semibold shrink-0`}>{s.label}</Badge>
              </div>
              <p className="text-2xl font-heading font-bold mt-2" data-testid={`sync-count-${d.key}`}>
                {(d.count || 0).toLocaleString("en-IN")} <span className="text-xs font-normal text-muted-foreground">{d.unit}</span>
              </p>
              <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                <p>Latest data point: <span className="font-medium text-foreground">{fmtDate(d.latest_data)}</span></p>
                <p>Last sync: <span className="font-medium text-foreground">{fmtDate(d.last_sync)}</span></p>
                <p className="pt-1">{d.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
